/**
 * Shared-service batch + cache (DataLoader).
 *
 * A coalescing/memoizing layer for the shared lookup services
 * (project-grant-form, project-grant-task, subSegmentation, general-ledger-lookup).
 * It removes the per-row N+1 lookup storm on the high-volume payment / cashiering
 * forms (Multiple Vendor Payment, Bulk Cash Receipt, Transaction Assignment) without
 * any per-form change: each single-item lookup delegates to a loader, and the loader
 * turns a tick's worth of keys into one batch request against the backend
 * `get-all-by-accounts` / batch endpoints.
 *
 * Two behaviours, matching the two call shapes the consuming code uses:
 *
 *  - Concurrent fan-out (a loop that subscribes without awaiting): every key
 *    requested in the same microtask tick is buffered and flushed as ONE batch call.
 *
 *  - Repeated keys across ticks (re-render / manual-add / edit): the per-key cache
 *    serves them from memory, so many rows that share a key collapse to one request
 *    per distinct key.
 *
 * For sequential `await` loops (import, budget-validate) the cache still dedups, but
 * to reach a single round trip call `loadMany()` once with the distinct keys.
 *
 * `load()` / `loadMany()` are promise-based so the core stays framework-light and
 * unit-testable; `load$()` adapts a single key to an Observable for RxJS callers.
 */
import { Observable, defer, from } from 'rxjs';

export type BatchFn<V> = (keys: string[]) => Promise<Map<string, V>>;

export interface BatchCacheLoaderOptions<V = unknown> {
	/** Max keys per outbound batch request. Larger keysets split into multiple calls. */
	maxBatchSize?: number;
	/**
	 * Value to resolve when the batch response omits a requested key, instead of rejecting.
	 * The key is still evicted from the cache so a later load retries.
	 */
	defaultForMissingKey?: () => V;
}

export class BatchCacheLoader<V> {
	private readonly cache = new Map<string, Promise<V>>();
	private readonly maxBatchSize: number;

	private batchKeys: string[] = [];
	private batchResolvers = new Map<string, { resolve: (v: V) => void; reject: (e: unknown) => void }>();
	private scheduled = false;

	/** Outbound batch requests issued. Exposed so callers/tests can assert the N+1 collapse. */
	private _batchCallCount = 0;

	constructor(private readonly batchFn: BatchFn<V>, private readonly options: BatchCacheLoaderOptions<V> = {}) {
		this.maxBatchSize = options.maxBatchSize ?? 200;
	}

	get batchCallCount(): number {
		return this._batchCallCount;
	}

	/**
	 * Concurrent fan-out path. Keys requested in the same tick coalesce into one
	 * batch; repeated keys (this tick or any later tick) resolve from cache.
	 */
	load(key: string): Promise<V> {
		const cached = this.cache.get(key);
		if (cached) {
			return cached;
		}

		const promise = new Promise<V>((resolve, reject) => {
			this.batchResolvers.set(key, { resolve, reject });
		});
		this.cache.set(key, promise);
		this.batchKeys.push(key);
		this.schedule();
		return promise;
	}

	/**
	 * Observable adapter over `load()` for RxJS call sites. Wrapped in `defer` so the observable stays
	 * cold: nothing is enqueued or fetched until something subscribes (matching HttpClient semantics and
	 * avoiding an unhandled rejection if a caller builds the observable but never subscribes). Same-tick
	 * subscribers still coalesce into one batch.
	 */
	load$(key: string): Observable<V> {
		return defer(() => from(this.load(key)));
	}

	/**
	 * Sequential-loop path. Resolves every key, reusing the cache for ones already
	 * fetched and issuing a single batch (chunked) for the rest. Use this from the
	 * import / budget-validate loops: gather distinct keys -> loadMany once -> read map.
	 */
	async loadMany(keys: string[]): Promise<Map<string, V>> {
		const results = await Promise.all(keys.map((key) => this.load(key)));
		const map = new Map<string, V>();
		keys.forEach((key, i) => map.set(key, results[i]));
		return map;
	}

	/** Drop the memoized entries (e.g. on client/tenant switch, or per validation pass). */
	clear(): void {
		this.cache.clear();
	}

	private schedule(): void {
		if (this.scheduled) {
			return;
		}
		this.scheduled = true;
		queueMicrotask(() => void this.flush());
	}

	private async flush(): Promise<void> {
		const keys = this.batchKeys;
		const resolvers = this.batchResolvers;
		this.batchKeys = [];
		this.batchResolvers = new Map();
		this.scheduled = false;

		for (let i = 0; i < keys.length; i += this.maxBatchSize) {
			const chunk = keys.slice(i, i + this.maxBatchSize);
			this._batchCallCount++;
			try {
				const result = await this.batchFn(chunk);
				for (const key of chunk) {
					if (result.has(key)) {
						resolvers.get(key)!.resolve(result.get(key) as V);
					} else if (this.options.defaultForMissingKey) {
						// Evict so a later load retries instead of memoizing the omission.
						this.cache.delete(key);
						resolvers.get(key)!.resolve(this.options.defaultForMissingKey());
					} else {
						// No default configured: surface the omission; evict so a later load retries.
						this.cache.delete(key);
						resolvers.get(key)!.reject(new Error(`BatchCacheLoader: batch response is missing key "${key}"`));
					}
				}
			} catch (error) {
				for (const key of chunk) {
					this.cache.delete(key); // allow retry; don't memoize a failure
					resolvers.get(key)!.reject(error);
				}
			}
		}
	}
}
